
// client/src/components/payment/PaymentUploader.jsx

import React, { useState } from "react";

export default function PaymentUploader({ file, setFile, onProcess, loading }) {

  const [hovering, setHovering] = useState(false);

  const handleDragOver  = (e) => { e.preventDefault(); setHovering(true);  };
  const handleDragLeave = ()    => setHovering(false);
  const handleDrop      = (e)   => {
    e.preventDefault();
    setHovering(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };

  return (
    <div style={styles.card}>

      {/* ── CARD HEADER ── */}
      <div style={styles.cardHeader}>
        <div style={styles.iconWrap}>
          <span style={styles.icon}>📄</span>
        </div>
        <div>
          <div style={styles.cardTitle}>Select File</div>
          <div style={styles.cardSubtitle}>Supports .xlsx and .xls formats</div>
        </div>
      </div>

      <div style={styles.divider} />

      {/* ── DROP ZONE ── */}
      <div
        style={{
          ...styles.dropZone,
          ...(hovering ? styles.dropZoneHover : {}),
          ...(file     ? styles.dropZoneActive : {}),
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* invisible full-area file input */}
        <input
          type="file"
          accept=".xlsx,.xls"
          style={styles.fileInput}
          onChange={(e) => setFile(e.target.files[0])}
        />

        <div style={styles.dropBody}>

          {/* upload cloud SVG */}
          <div style={{
            ...styles.uploadIconCircle,
            ...(file ? styles.uploadIconCircleActive : {}),
          }}>
            <svg
              width="26" height="26" viewBox="0 0 24 24"
              fill="none" stroke={file ? "#2563eb" : "#9ca3af"}
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="16 16 12 12 8 16" />
              <line x1="12" y1="12" x2="12" y2="21" />
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            </svg>
          </div>

          {file ? (
            /* FILE CHOSEN STATE */
            <>
              <div style={styles.dropLabel}>File ready to process</div>
              <div style={styles.fileNamePill}>
                <span style={styles.fileNamePillIcon}>📎</span>
                <span style={styles.fileNamePillText}>{file.name}</span>
                <button
                  style={styles.fileRemoveBtn}
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  title="Remove"
                >✕</button>
              </div>
              <div style={styles.dropHint}>Click or drag to replace</div>
            </>
          ) : (
            /* EMPTY STATE */
            <>
              <div style={styles.dropLabel}>
                {hovering ? "Release to upload" : "Drag & drop your Excel file"}
              </div>
              <div style={styles.dropHint}>or click anywhere here to browse</div>
              <div style={styles.dropFormats}>
                <span style={styles.formatTag}>.xlsx</span>
                <span style={styles.formatTag}>.xls</span>
              </div>
            </>
          )}

        </div>
      </div>

      {/* ── PROCESS BUTTON ── */}
      <button
        style={{
          ...styles.processBtn,
          ...(!file || loading ? styles.processBtnDisabled : {}),
        }}
        disabled={!file || loading}
        onClick={onProcess}
      >
        {loading
          ? <><span style={styles.spinner} />Processing…</>
          : "Process Payment →"
        }
      </button>

    </div>
  );
}

// ─────────────────────────────────────
// STYLES
// ─────────────────────────────────────
const styles = {

  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    boxSizing: "border-box",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  // ── HEADER ──
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },
  iconWrap: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    backgroundColor: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  icon: { fontSize: "18px" },
  cardTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#111827",
    letterSpacing: "-0.01em",
  },
  cardSubtitle: {
    fontSize: "12px",
    color: "#9ca3af",
    marginTop: "2px",
    fontWeight: "400",
  },

  divider: {
    height: "1px",
    backgroundColor: "#f3f4f6",
    margin: "0 -24px",
  },

  // ── DROP ZONE ──
  dropZone: {
    position: "relative",
    border: "1.5px dashed #e5e7eb",
    borderRadius: "10px",
    backgroundColor: "#fafafa",
    minHeight: "220px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "border-color 0.2s, background-color 0.2s",
    overflow: "hidden",
  },
  dropZoneHover: {
    borderColor: "#6366f1",
    backgroundColor: "#f5f3ff",
  },
  dropZoneActive: {
    borderColor: "#2563eb",
    backgroundColor: "#eff6ff",
  },

  fileInput: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    opacity: 0,
    cursor: "pointer",
    zIndex: 2,
  },

  dropBody: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    padding: "32px 24px",
    textAlign: "center",
    pointerEvents: "none",
    zIndex: 1,
  },

  uploadIconCircle: {
    width: "52px",
    height: "52px",
    borderRadius: "12px",
    backgroundColor: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "4px",
    transition: "background-color 0.2s",
  },
  uploadIconCircleActive: {
    backgroundColor: "#dbeafe",
  },

  dropLabel: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#111827",
    letterSpacing: "-0.01em",
  },
  dropHint: {
    fontSize: "12px",
    color: "#9ca3af",
    fontWeight: "400",
  },

  dropFormats: {
    display: "flex",
    gap: "6px",
    marginTop: "6px",
  },
  formatTag: {
    padding: "3px 8px",
    backgroundColor: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#6b7280",
    letterSpacing: "0.02em",
  },

  // ── FILE CHOSEN STATE (inside drop zone) ──
  fileNamePill: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 10px 6px 10px",
    backgroundColor: "#eff6ff",
    border: "1px solid #dbeafe",
    borderRadius: "6px",
    pointerEvents: "all",  // allow the ✕ button to work
  },
  fileNamePillIcon: { fontSize: "13px" },
  fileNamePillText: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#1d4ed8",
    maxWidth: "220px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileRemoveBtn: {
    background: "none",
    border: "none",
    color: "#93c5fd",
    fontSize: "11px",
    cursor: "pointer",
    padding: "0 2px",
    lineHeight: 1,
    flexShrink: 0,
    pointerEvents: "all",
  },

  // ── PROCESS BUTTON ──
  processBtn: {
    width: "100%",
    height: "40px",
    fontSize: "13px",
    fontWeight: "700",
    color: "#ffffff",
    backgroundColor: "#2563eb",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    letterSpacing: "-0.01em",
    transition: "background-color 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  processBtnDisabled: {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
    cursor: "not-allowed",
  },

  spinner: {
    width: "13px",
    height: "13px",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid #ffffff",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    display: "inline-block",
  },
};